import { App } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { dueTasks } from '@/domain/worklog';
import { useNow } from '@/hooks/use-now';
import { useWorklog } from '@/hooks/use-records';
import { useCurrentUser } from '@/stores/session-store';
import { isBrowserNotificationEnabled } from './notification-store';
import { useDailyScheduleGeneration } from './use-schedule-generation';

/**
 * Chạy ngầm ở MỌI màn hình (gắn một lần trong khung ứng dụng): sinh việc theo lịch lúc vào app và
 * lúc sang ngày; báo «Đến giờ» đúng MỘT lần cho mỗi việc vừa tới giờ nhắc.
 *
 * Chỉ nhắc việc CỦA CHÍNH người đang đăng nhập. Bản cũ nhắc mọi việc trong phạm vi nhìn — hợp với
 * vài người, nhưng với ~100 nhân sự thì quản lý bị dội thông báo về việc mà họ không phải là người
 * làm. Việc quá giờ của cấp dưới vẫn xem được ở thanh «ĐẾN GIỜ» và huy hiệu của màn Nhật ký.
 */
export function DueTaskNotifier() {
  const now = useNow();
  const { username } = useCurrentUser();
  const worklog = useWorklog();
  const entries = useMemo(
    () => worklog.filter((entry) => entry.owner === username),
    [worklog, username],
  );
  const { notification } = App.useApp();
  const announced = useRef(new Set<number>());
  const armed = useRef(false);

  useDailyScheduleGeneration(now.date);

  useEffect(() => {
    const due = dueTasks(entries, now);
    // Lượt đầu chỉ GHI NHỚ những việc vốn đã quá giờ từ trước khi mở app — không dội một tràng
    // thông báo vào mặt người vừa đăng nhập. Thanh «ĐẾN GIỜ» ở màn Nhật ký vẫn hiện chúng.
    if (!armed.current) {
      armed.current = true;
      due.forEach((entry) => announced.current.add(entry.recordId));
      return;
    }
    for (const entry of due) {
      if (announced.current.has(entry.recordId)) continue;
      announced.current.add(entry.recordId);
      notification.warning({
        title: 'Đến giờ',
        description: `${entry.time} — ${entry.text}`,
        placement: 'bottomRight',
      });
      if (isBrowserNotificationEnabled() && 'Notification' in window) {
        try {
          new Notification('SUN MUSIC', {
            body: `${entry.time} — ${entry.text}`,
            tag: `worklog-${entry.recordId}`,
          });
        } catch {
          /* trình duyệt từ chối: thông báo trong app ở trên là đủ */
        }
      }
    }
  }, [entries, now, notification]);

  return null;
}
